import dialCodes from "../assets/misc/dialCodes.json";

/**
 * A picker entry: the flag and dial code shown on the button, plus the ISO-2
 * country code, which is what libphonenumber needs to interpret a number typed
 * in national format (see utils/phone).
 */
export interface DialCode {
  flag: string;
  dial_code: string;
  code: string;
}

interface DialCodeEntry extends DialCode {
  name: string;
}

/** Default when the location gives us nothing — ZA is the predominant market. */
export const FALLBACK_DIAL_CODE: DialCode = {
  flag: "🇿🇦",
  dial_code: "+27",
  code: "ZA",
};

const ENTRIES: DialCodeEntry[] = (
  dialCodes as { title: string; data: DialCodeEntry[] }[]
).flatMap((group) => group.data);

const toDialCode = (entry: DialCodeEntry): DialCode => ({
  flag: entry.flag,
  dial_code: entry.dial_code,
  code: entry.code,
});

/** ISO-3166 alpha-2 → picker entry. Country codes are unique in dialCodes.json. */
const BY_COUNTRY: Record<string, DialCode> = Object.fromEntries(
  ENTRIES.map((entry) => [entry.code, toDialCode(entry)]),
);

// Territories share dial codes (+44 also covers Guernsey/Jersey/Isle of Man,
// +1 covers Canada, +61 covers Christmas/Cocos Islands). Only needed when a
// number's exact country can't be determined — libphonenumber leaves `country`
// undefined for reserved/fictional ranges — so we fall back to the country the
// calling code overwhelmingly means, rather than whichever sorts first.
const PRIMARY_BY_DIAL: Record<string, string> = {
  "+1": "US",
  "+7": "RU",
  "+44": "GB",
  "+47": "NO",
  "+61": "AU",
};

const BY_DIAL: Record<string, DialCode> = ENTRIES.reduce<
  Record<string, DialCode>
>((acc, entry) => {
  const primary = PRIMARY_BY_DIAL[entry.dial_code];
  if (!acc[entry.dial_code] || entry.code === primary) {
    acc[entry.dial_code] = toDialCode(entry);
  }
  return acc;
}, {});

/** The picker entry for an ISO-3166 alpha-2 country code, or null if unknown. */
export const dialCodeForCountry = (
  country: string | null | undefined,
): DialCode | null =>
  country ? (BY_COUNTRY[country.trim().toUpperCase()] ?? null) : null;

/** The picker entry for a calling code such as "44", used when the exact
 * country of a number can't be resolved. */
export const dialCodeForCallingCode = (
  callingCode: string | null | undefined,
): DialCode | null => (callingCode ? (BY_DIAL[`+${callingCode}`] ?? null) : null);

/** Human-readable country name for a picker entry, for error copy. */
export const countryNameFor = (dialCode: DialCode): string =>
  ENTRIES.find((entry) => entry.code === dialCode.code)?.name ?? "that country";

/**
 * The dial code a phone input should start on: the location's own country,
 * since a customer scanning a venue's QR code is almost always standing in it.
 *
 * Deliberately no browser-locale guess in between — South African phones
 * commonly run en-US/en-GB locales, so that guess would send our biggest
 * market to the wrong flag. Where we can't tell, South Africa is the better
 * bet, and legacy locations with no `country` set are overwhelmingly ZA.
 */
export const resolveDefaultDialCode = (
  locationCountry?: string | null,
): DialCode => dialCodeForCountry(locationCountry) ?? FALLBACK_DIAL_CODE;
